import {
  listUsersQuery,
  getUserQuery,
  getUserByUsernameQuery,
  getUserByEmailQuery,
  createUserMutation,
  updateUserMutation,
  searchUsersQuery
} from '../graphql/user';
import {
  toggleFollowMutation,
  getUserFollowersQuery,
  getUserFollowingQuery
} from '../graphql/follow.js';
import * as postGql from '../graphql/post';

// src/services/api.js
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import axios from 'axios';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};
const ENABLE_GRAPHQL_ERROR_LOGGING = true;

// URL cache to prevent repeated API calls
const urlCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// GraphQL Queries and Mutations
const updateMovieInteractionMutation = /* GraphQL */ `
  mutation UpdateMovieInteraction(
    $movieId: ID!,
    $watched: Boolean,
    $notInterested: Boolean,
    $userRating: Float
  ) {
    updateMovieInteraction(
      input: {
        movieId: $movieId,
        watched: $watched,
        notInterested: $notInterested,
        userRating: $userRating
      }
    ) {
      id
      movie {
        id
        name
      }
      watched
      notInterested
      userRating
      updatedAt
    }
  }
`;

const sharePostMutation = /* GraphQL */ `
  mutation SharePost($postID: ID!, $message: String) {
    sharePost(postID: $postID, message: $message) {
      id
      userID
      postID
      message
      createdAt
      user {
        id
        username
        displayName
        avatar
      }
      post {
        id
        content
        author {
          id
          username
          displayName
          avatar
        }
      }
    }
  }
`;


// API Functions

export const getPosts = async (limit = 20, nextToken = null) => {
  try {
    const response = await getClient().graphql({
      query: postGql.listPostsQuery,
      variables: { limit, nextToken },
    });
    return response.data.listPosts;
  } catch (error) {
    console.error('Error fetching posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
}
// ...existing code...


export const deletePost = async (id) => {
  try {
    const result = await getClient().graphql({
      query: postGql.deletePostMutation,
      variables: { id },
    });
    return result.data.deletePost;
  } catch (error) {
    console.error('Error deleting post:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};



export const createPost = async (postData) => {
  try {
    // Only allowed fields for CreatePostInput
    const allowedFields = [
      'type', 'content', 'images', 'video', 'links', 'hashtags', 'visibility', 'authorID', 'authorIdentityId'
    ];
    const input = {};
    for (const field of allowedFields) {
      if (postData[field] !== undefined && postData[field] !== null) {
        input[field] = postData[field];
      }
    }
    const response = await getClient().graphql({
      query: postGql.createPostMutation,
      variables: { input },
    });
    // console.log('createPost: Successfully created post with ID:', response.data.createPost.id);
    return response.data.createPost;
  } catch (error) {
    console.error('Error creating post:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
}

export const toggleLike = async (postID) => {
  try {
    const response = await getClient().graphql({
      query: postGql.toggleLikeMutation,
      variables: { postID },
    });
    return response.data.toggleLike;
  } catch (error) {
    console.error('Error toggling like:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};







// File upload for images/videos

export const uploadFile = async (file, key, progressCallback, accessLevel = 'protected') => {
  // console.log(`Starting upload for file: ${file.name} to key: ${key}`);
  try {
   const result = await uploadData({
    key,
    data: file,
    options: {
      contentType: file.type,
      level: accessLevel,
      onProgress: progressCallback
        ? ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const progress = Math.round((transferredBytes / totalBytes) * 100);
              progressCallback(progress);
            }
          }
        : undefined,
    },
  });
    // console.log("Amplify upload job started:", result);
    // The result of the upload job is a promise that must be awaited.
    const finalResult = await result.result;
    // console.log("Amplify upload job finished:", finalResult);
    // For private uploads, the finalResult.path should contain the full path.
    // If it doesn't, we fall back to the original key. The getUrl function will then use the
    // currently signed-in user's identityId to prefix the key, which works for new uploads.
    // The key is what we want to store in the DB.
    return finalResult.key;
  } catch (error) {
    console.error('Error uploading file:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const getFileUrl = async (key, targetIdentityId = null, accessLevel = 'protected') => {
  // console.log(`Getting URL for key: ${key}`, `Target IdentityID: ${targetIdentityId}`);

  if (!key || typeof key !== 'string') {
    console.error("getFileUrl error: A valid key must be provided.", { key });
    throw new Error("A valid key must be provided to getFileUrl.");
  }

  // Create cache key
  const cacheKey = `${key}-${targetIdentityId || 'default'}-${accessLevel}`;
  
  // Check cache first
  const cached = urlCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    // console.log(`Using cached URL for key ${key}`);
    return cached.url;
  }

  try {
    const options = {
      key,
      level: accessLevel,  // Must match upload accessLevel
      expiresIn: 604800, // URL expires in 7 days (maximum allowed)
      validateObjectExistence: false, // Don't validate existence to avoid bucket errors
    };

    // For protected files, we need to specify the targetIdentityId
    if (accessLevel === 'protected' && targetIdentityId) {
      options.targetIdentityId = targetIdentityId;
    }

    const urlData = await getUrl(options);

    // console.log(`Successfully generated URL for key ${key}:`, urlData);

    const url = urlData.url.href;
    
    // Cache the URL
    urlCache.set(cacheKey, {
      url,
      timestamp: Date.now()
    });
    
    // Clean up old cache entries periodically
    if (urlCache.size > 100) {
      const now = Date.now();
      for (const [key, value] of urlCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          urlCache.delete(key);
        }
      }
    }

    // Amplify v6+ returns URL object in urlData.url
    return url;

  } catch (error) {
    console.error('Error getting file URL:', error);
    // Return null instead of throwing to prevent UI crashes
    return null;
  }
};

export const deleteFile = async (key, accessLevel = 'protected') => {
  try {
    await remove({ key, options: { level: accessLevel } });
    return true;
  } catch (error) {
    console.error('Error deleting file:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};



// Search cache for production performance
const searchCache = new Map();
const SEARCH_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

export const searchUsers = async (searchTerm, limit = 20, nextToken = null) => {
  if (!searchTerm || searchTerm.trim() === '') return { items: [], nextToken: null };
  
  const term = searchTerm.trim().toLowerCase();
  if (term.length < 1) return { items: [], nextToken: null };
  if (limit > 50) limit = 50;
  
  const cacheKey = `${term}-${limit}-${nextToken || 'null'}`;
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < SEARCH_CACHE_DURATION) {
    return cached.data;
  }
  
  try {
    // Try beginsWith first (most reliable in DynamoDB)
    let filter = {
      or: [
        { username_lowercase: { beginsWith: term } },
        { displayName_lowercase: { beginsWith: term } }
      ]
    };

    let result = await getClient().graphql({
      query: searchUsersQuery,
      variables: { filter, limit, nextToken },
    });

    let users = result.data.listUsers;
    
    // If no results with beginsWith, try contains
    if (users.items.length === 0 && term.length >= 2 && !nextToken) {
      filter = {
        or: [
          { username_lowercase: { contains: term } },
          { displayName_lowercase: { contains: term } }
        ]
      };
      
      result = await getClient().graphql({
        query: searchUsersQuery,
        variables: { filter, limit, nextToken },
      });
      
      users = result.data.listUsers;
    }
    
    // If still no results, fallback to client-side filtering
    if (users.items.length === 0 && !nextToken) {
      const allUsers = await getClient().graphql({
        query: searchUsersQuery,
        variables: { limit: 100 },
      });
      
      users = {
        items: allUsers.data.listUsers.items.filter(u => 
          (u.username && u.username.toLowerCase().includes(term)) ||
          (u.displayName && u.displayName.toLowerCase().includes(term))
        ).slice(0, limit),
        nextToken: null
      };
    }

    // Cache result
    searchCache.set(cacheKey, { data: users, timestamp: Date.now() });
    
    // Clean cache
    if (searchCache.size > 100) {
      const now = Date.now();
      for (const [key, value] of searchCache.entries()) {
        if (now - value.timestamp > SEARCH_CACHE_DURATION) {
          searchCache.delete(key);
        }
      }
    }

    return users;
  } catch (error) {
    console.error('Error searching users:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    return { items: [], nextToken: null };
  }
};

export const searchPosts = async (searchTerm, limit = 20, nextToken = null) => {
  if (!searchTerm || searchTerm.trim() === '') return { items: [], nextToken: null };
  try {
    const lowercasedTerm = searchTerm.toLowerCase();
    const filter = {
      or: [
        { content: { contains: lowercasedTerm } },
        { hashtags: { contains: lowercasedTerm } }
      ]
    };

    const result = await getClient().graphql({
      query: postGql.searchPostsQuery,
      variables: { filter, limit, nextToken },
    });

    return result.data.listPosts;
  } catch (error) {
    console.error('Error searching posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const getUserFollowing = async (userID) => {
  try {
    const result = await getClient().graphql({
      query: getUserFollowingQuery,
      variables: { userID },
    });
    return result.data.listFollows.items;
  } catch (error) {
    console.error('Error fetching user following:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const getUserFollowers = async (userID) => {
  try {
    const result = await getClient().graphql({
      query: getUserFollowersQuery,
      variables: { userID },
    });
    return result.data.listFollows.items;
  } catch (error) {
    console.error('Error fetching user followers:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


// Function to fetch posts for a specific user (author)
export const getUserPosts = async (authorID, limit = 20, nextToken = null) => {
  if (!authorID) return { items: [], nextToken: null };
  try {
    const result = await getClient().graphql({
      query: postGql.getUserPostsQuery,
      variables: {
        filter: { authorID: { eq: authorID } },
        limit,
        nextToken,
      },
    });
    return result.data.listPosts;
  } catch (error) {
    console.error('Error fetching user posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const listUsers = async (limit = 20, nextToken = null) => {
  try {
    const result = await getClient().graphql({
      query: listUsersQuery,
      variables: { limit, nextToken },
    });
    return result.data.listUsers;
  } catch (error) {
    console.error("Error listing users:", error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

/**
 * Fetches a user by their username. Note: The underlying query uses an index.
 * @param {string} username The username to search for.
 * @returns {object|null} The first user found or null.
 */
export const getUserByUsername = async (username) => {
  try {
    const result = await getClient().graphql({
      query: getUserByUsernameQuery,
      variables: { username },
    });
    return result.data.userByUsername.items[0] || null;
  } catch (error) {
    console.error('Error getting user by username:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const getUserByEmail = async (email) => {
  try {
    const result = await getClient().graphql({
      query: getUserByEmailQuery,
      variables: { email },
    });
    return result.data.listUsers.items[0] || null;
  } catch (error) {
    console.error('Error getting user by email:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    // Only allowed fields for CreateUserInput
    const allowedFields = [ 'id', 'identityId', 'username', 'username_lowercase', 'email', 'displayName', 'displayName_lowercase', 'avatar', 'bio', 'website', 'location', 'role', 'interests', 'languagePreferences', 'coverPhoto' ];
    const validRoles = ['ADMIN', 'MODERATOR', 'USER'];
    const input = {};
    
    // Try to get role from Cognito groups if available
    let roleFromGroup = null;
    if (userData.signInDetails && userData.signInDetails.groups && Array.isArray(userData.signInDetails.groups)) {
      for (const group of userData.signInDetails.groups) {
        if (validRoles.includes(group)) {
          roleFromGroup = group;
          break;
        }
      }
    }
    
    // Build input with only defined fields
    for (const field of allowedFields) {
      if (field === 'role') {
        const roleVal = roleFromGroup || userData.role;
        input.role = validRoles.includes(roleVal) ? roleVal : 'USER';
      } else if (userData[field] !== undefined && userData[field] !== null && userData[field] !== '') {
        input[field] = userData[field];
      }
    }
    
    // Ensure required fields are present
    if (!input.id || !input.email || !input.username) {
      throw new Error('Missing required fields: id, email, or username');
    }
    
    const result = await getClient().graphql({
      query: createUserMutation,
      variables: { input },
    });
    return result.data.createUser;
  } catch (error) {
    console.error('Error creating user:', error);
    
    // Handle ConditionalCheckFailedException - user already exists
    if (error.errors && error.errors.some(err => 
      err.errorType === 'DynamoDB:ConditionalCheckFailedException' ||
      err.message?.includes('ConditionalCheckFailedException')
    )) {
      console.log('User already exists, attempting to fetch existing user');
      // Try to fetch the existing user
      try {
        if (userData.email) {
          const existingUser = await getUserByEmail(userData.email);
          if (existingUser) {
            console.log('Found existing user by email:', existingUser.id);
            return existingUser;
          }
        }
        if (userData.id) {
          const existingUser = await getUser(userData.id);
          if (existingUser) {
            console.log('Found existing user by ID:', existingUser.id);
            return existingUser;
          }
        }
      } catch (fetchError) {
        console.error('Failed to fetch existing user:', fetchError);
      }
    }
    
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const updateUser = async (id, updateData) => {
  try {
    // Define fields that are allowed in the UpdateUserInput type in your schema
    const allowedFields = [
      'displayName', 'bio', 'website', 'location', 'interests', 'languagePreferences', 'watchlistPublic', 'avatar', 'coverPhoto', 'activeBadges', 'badgeDisplaySettings'
    ];
    const input = { id: id };

    // Build the input object with only the allowed fields that have a value
    for (const field of allowedFields) {
      if (updateData[field] !== undefined) {
        input[field] = updateData[field];
      }
    }
    
    // If displayName is being updated, also update the lowercase version
    if (updateData.displayName !== undefined) {
      input.displayName_lowercase = updateData.displayName.toLowerCase();
    }

    const result = await getClient().graphql({
      query: updateUserMutation,
      variables: { input },
    });
    return result.data.updateUser;
  } catch (error) {
    console.error('Error updating user:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const getUser = async (id) => {
  try {
    const result = await getClient().graphql({
      query: getUserQuery,
      variables: { id },
    });
    return result.data.getUser;
  } catch (error) {
    console.error("Error getting user:", error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};




export const toggleFollow = async (followeeID) => {
  try {
    const result = await getClient().graphql({
      query: toggleFollowMutation,
      variables: { followeeID },
    });
    return result.data.toggleFollow;
  } catch (error) {
    console.error('Error toggling follow:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const createComment = async (commentData) => {
  try {
    const result = await getClient().graphql({
      query: postGql.createCommentMutation,
      variables: { input: commentData },
    });
    return result.data.addComment;
  } catch (error) {
    console.error('Error creating comment:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const sharePost = async (postID, message = '') => {
  try {
    const result = await getClient().graphql({
      query: sharePostMutation,
      variables: { postID, message },
    });
    return result.data.sharePost;
  } catch (error) {
    console.error('Error sharing post:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};


export const getFeed = async (limit = 20, nextToken = null) => {
  try {
    const result = await getClient().graphql({
      query: postGql.getFeedQuery,
      variables: { limit, nextToken },
    });
    // Returns { items, nextToken }
    const feedData = result.data.feed;
    if (!feedData || !feedData.items) {
      return { items: [], nextToken: null };
    }
    return { items: feedData.items, nextToken: feedData.nextToken };
  } catch (err) {
    console.error("Error fetching feed:", err);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && err.errors) {
      for (const e of err.errors) {
        console.error('GraphQL error:', e.message, e);
      }
    }
    return { items: [], nextToken: null };
  }
};

export const getTrendingPosts = async (limit = 20, nextToken = null) => {
  try {
    // Use the correct query from mutations.js
    const trendingQuery = /* GraphQL */ `
      query TrendingPosts($limit: Int, $nextToken: String) {
        trendingPosts(limit: $limit, nextToken: $nextToken) {
          items {
            id
            type
            authorID
            authorIdentityId
            content
            images
            video
            links
            hashtags
            visibility
            likesCount
            commentsCount
            sharesCount
            createdAt
            updatedAt
            author {
              id
              username
              displayName
              avatar
              verified
            }
          }
          nextToken
        }
      }
    `;
    
    const variables = { limit, nextToken };
    const result = await getClient().graphql({ query: trendingQuery, variables });
    return result.data.trendingPosts;
  } catch (error) {
    console.error('Error fetching trending posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    // Return empty result instead of throwing to prevent UI crashes
    return { items: [], nextToken: null };
  }
};

export const getPublicPosts = async (limit = 20, nextToken = null) => {
  try {
    const variables = { limit, nextToken };
    const result = await getClient().graphql({ query: postGql.getPublicPostsQuery, variables });
    // Show all posts that are public (null/undefined visibility treated as followers for backward compatibility)
    const publicPosts = {
      items: result.data.listPosts.items.filter(post => 
        post.visibility === 'PUBLIC'
      ),
      nextToken: result.data.listPosts.nextToken
    };
    return publicPosts;
  } catch (error) {
    console.error('Error fetching public posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const updateMovieInteraction = async ({
  movieId,
  watched = null,
  notInterested = null,
  userRating = null,
}) => {
  try {
    const variables = { movieId, watched, notInterested, userRating };
    // Remove undefined keys for cleaner mutation call
    Object.keys(variables).forEach(
      (key) => variables[key] === null && delete variables[key]
    );

    const response = await getClient().graphql({
      query: updateMovieInteractionMutation,
      variables,
    });
    return response.data.updateMovieInteraction;
  } catch (error) {
    console.error('Error updating movie interaction:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

// Update Post API
export const updatePost = async (postData) => {
  try {
    const allowedFields = [
      'id', 'content', 'images', 'video', 'links', 'hashtags', 'visibility'
    ];
    const input = {};
    for (const field of allowedFields) {
      if (postData[field] !== undefined && postData[field] !== null) {
        input[field] = postData[field];
      }
    }
    const response = await getClient().graphql({
      query: postGql.updatePostMutation,
      variables: { input },
    });
    return response.data.updatePost;
  } catch (error) {
    console.error('Error updating post:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const reportPost = async (reportData) => {
  try {
    // Get post details first to notify author
    const postResult = await getClient().graphql({
      query: `
        query GetPost($id: ID!) {
          getPost(id: $id) {
            id
            authorID
            content
          }
        }
      `,
      variables: { id: reportData.postID },
    });
    
    const result = await getClient().graphql({
      query: `
        mutation CreatePostReport($input: CreatePostReportInput!) {
          createPostReport(input: $input) {
            id
            postID
            reason
            description
            status
            createdAt
          }
        }
      `,
      variables: { input: reportData },
    });
    
    // Create notification for reporter
    await createNotification({
      userID: reportData.reportedBy,
      type: 'INFO',
      message: `Your report for post has been submitted and is under review.`,
      isRead: false,
      metadata: JSON.stringify({ reportId: result.data.createPostReport.id, type: 'post_report' })
    });
    
    // Create notification for post author
    if (postResult.data.getPost?.authorID && postResult.data.getPost.authorID !== reportData.reportedBy) {
      await createNotification({
        userID: postResult.data.getPost.authorID,
        type: 'WARNING',
        message: `Your post has been reported for: ${reportData.reason}`,
        isRead: false,
        metadata: JSON.stringify({ reportId: result.data.createPostReport.id, postId: reportData.postID, type: 'post_reported' })
      });
    }
    
    return result.data.createPostReport;
  } catch (error) {
    console.error('Error reporting post:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const reportMovie = async (reportData) => {
  try {
    const result = await getClient().graphql({
      query: `
        mutation CreateMovieReport($input: CreateMovieReportInput!) {
          createMovieReport(input: $input) {
            id
            movieID
            reason
            description
            status
            createdAt
          }
        }
      `,
      variables: { input: reportData },
    });
    
    // Create notification for reporter
    await createNotification({
      userID: reportData.reportedBy,
      type: 'INFO',
      message: `Your report for movie has been submitted and is under review.`,
      isRead: false,
      metadata: JSON.stringify({ reportId: result.data.createMovieReport.id, type: 'movie_report' })
    });
    
    return result.data.createMovieReport;
  } catch (error) {
    console.error('Error reporting movie:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const reportShow = async (reportData) => {
  try {
    const result = await getClient().graphql({
      query: `
        mutation CreateShowReport($input: CreateShowReportInput!) {
          createShowReport(input: $input) {
            id
            showID
            reason
            description
            status
            createdAt
          }
        }
      `,
      variables: { input: reportData },
    });
    
    // Create notification for reporter
    await createNotification({
      userID: reportData.reportedBy,
      type: 'INFO',
      message: `Your report for show has been submitted and is under review.`,
      isRead: false,
      metadata: JSON.stringify({ reportId: result.data.createShowReport.id, type: 'show_report' })
    });
    
    return result.data.createShowReport;
  } catch (error) {
    console.error('Error reporting show:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

// Report notification functions
export const createNotification = async (notificationData) => {
  try {
    const result = await getClient().graphql({
      query: `
        mutation CreateNotification($input: CreateNotificationInput!) {
          createNotification(input: $input) {
            id
            userID
            type
            message
            isRead
            metadata
            createdAt
          }
        }
      `,
      variables: { input: notificationData },
    });
    return result.data.createNotification;
  } catch (error) {
    console.error('Error creating notification:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

export const updateReportStatus = async (reportType, reportId, status) => {
  try {
    const mutations = {
      post: 'updatePostReport',
      movie: 'updateMovieReport', 
      show: 'updateShowReport'
    };
    
    const result = await getClient().graphql({
      query: `
        mutation Update${reportType.charAt(0).toUpperCase() + reportType.slice(1)}Report($input: Update${reportType.charAt(0).toUpperCase() + reportType.slice(1)}ReportInput!) {
          ${mutations[reportType]}(input: $input) {
            id
            status
            updatedAt
          }
        }
      `,
      variables: { 
        input: { 
          id: reportId, 
          status 
        } 
      },
    });
    return result.data[mutations[reportType]];
  } catch (error) {
    console.error('Error updating report status:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    throw error;
  }
};

// Secure getUserPosts function that calls Lambda
export const getUserPostsSecure = async (authorID, limit = 20, nextToken = null) => {
  if (!authorID) return { items: [], nextToken: null };
  try {
    const result = await getClient().graphql({
      query: /* GraphQL */ `
        query GetUserPostsSecure($authorID: ID!, $limit: Int, $nextToken: String) {
          getUserPostsSecure(authorID: $authorID, limit: $limit, nextToken: $nextToken) {
            items {
              id
              content
              images
              video
              links
              hashtags
              visibility
              authorID
              authorIdentityId
              likesCount
              commentsCount
              sharesCount
              author { id username displayName avatar verified }
              likes { items { id userID } }
              comments(limit: 50) { items { id content authorID parentID createdAt author { id username displayName avatar } } }
              createdAt
              updatedAt
            }
            nextToken
          }
        }
      `,
      variables: { authorID, limit, nextToken },
    });
    return result.data.getUserPostsSecure;
  } catch (error) {
    console.error('Error fetching secure user posts:', error);
    if (ENABLE_GRAPHQL_ERROR_LOGGING && error.errors) {
      for (const err of error.errors) {
        console.error('GraphQL error:', err.message, err);
      }
    }
    // Fallback to frontend filtering
    return getUserPosts(authorID, limit, nextToken);
  }
};